import React, { useState } from 'react';
import { getUserContact } from '../services/userService';
import { listDealDocuments, uploadDealDocument } from '../services/dealDocuments';
import { useAuth } from '../context/AuthContext';
import { firstSigner } from '../services/signers';

// Seller under contract: the TC reviews the title initiation email, edits it if needed, and sends it.
// The email fills in from the under contract form, the seller form, and the co-list agent. Send opens the
// email with the To and CC lines filled in. The attachment step is still manual for now.

const TEMPLATE = `Hi {{title_agent_first_name}},

My name is {{tc_first_name}}, I'm {{agent_first_name}} {{agent_last_name}}'s Transaction Coordinator. We have a new file for you!
Attached is the contract for sale at {{property_address}}. We are working with the SELLERS. The Buyer's agent is {{cooperative_agent_first_name}} {{cooperative_agent_last_name}}

Contract Timeline:
·  Effective Date... {{uc_dt}}
·  Initial Earnest Deposit of {{earnest_money_amt}} due... {{earnest_money_due_dt}}
·  Due Diligence Deadline... {{due_diligence_deadline_dt}}
·  Second Earnest Money Deposit of {{second_emd_amount}} due... {{second_emd}}
·  Closing Date on... {{forecasted_closed_dt}}

Below is contact information for the file.

Listing Agent Information:
{{agent_first_name}} {{agent_last_name}}
{{agent_email}}
{{agent_phone_number}}

Additional Team Members to Copy (if applicable):
{{operations_name}}
{{operations_phone}}
{{operations_email}}

Please also CC me on all communication.

Seller's Contact Information: (copied on this email)

{{first_name}} {{last_name}}
{{email}}
{{phone}}

{{second_contact_name}}
{{second_contact_email}}
{{second_contact_phone}}

Buyer's Agent Information: (copied on this email)
{{cooperative_agent_first_name}} {{cooperative_agent_last_name}}
{{coop_agent_email}}
{{coop_agent_phone}}

Buyer Side Transaction Coordinator: (if Applicable)
{{coop_tc_name}}
{{coop_tc_email}}
{{coop_tc_phone}}

Buyer's Name (s):
{{buyer_names}}

We look forward to working with everyone!

--

{{tc_full_name}}
Transaction Coordinator
RESIDENCE | eXp Realty
{{tc_phone}}
{{tc_email}}`;

// Resale buyer under contract: same email process, with the buyer side copy from the TC's resale template.
const RESALE_TEMPLATE = `Hi {{title_agent_first_name}},

My name is {{tc_first_name}} {{tc_last_name}}, I'm {{agent_first_name}} {{agent_last_name}}'s Transaction Coordinator. We have a new file for you!

Attached is the contract for sale at {{property_address}}. We are working with the buyers. The listing agent is {{cooperative_agent_first_name}} {{cooperative_agent_last_name}}.

Contract Timeline:
·  Effective Date... {{uc_dt}}
·  Initial Earnest Deposit of {{earnest_money_amt}} due... {{earnest_money_due_dt}}
·  Due Diligence Deadline... {{due_diligence_deadline_dt}}
·  Second Earnest Money Deposit of {{second_emd_amount}} due... {{second_emd}}
·  Closing Date on... {{forecasted_closed_dt}}

Below is contact information for the file.

Buyer's Agent Information: (copied on this email)
{{agent_first_name}} {{agent_last_name}}
{{agent_email}}
{{agent_phone_number}}

{{co_agent_block}}Please also CC me on all communication.

Buyer's Contact Information: (copied on this email)
{{first_name}} {{last_name}}
{{email}}
{{phone}}

{{second_contact_first_name}} {{second_contact_last_name}}
{{second_contact_email}}
{{second_contact_phone}}

Listing Agent Information: (copied on this email)
{{cooperative_agent_first_name}} {{cooperative_agent_last_name}}
{{coop_agent_email}}
{{coop_agent_phone}}

Seller Side Transaction Coordinator: (if applicable)
{{coop_tc_name}}
{{coop_tc_email}}
{{coop_tc_phone}}

Seller's Name(s):
{{seller_names}}

We look forward to working with everyone!

--

{{tc_full_name}}
Transaction Coordinator
RESIDENCE | eXp Realty
{{tc_phone}}
{{tc_email}}`;

const fill = (tpl, map) => tpl.replace(/\{\{(\w+)\}\}/g, (_, k) => (map[k] ?? ''));
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(`${d}T00:00:00`);
  return isNaN(dt) ? '' : dt.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
};
const fmtMoney = (v) => {
  const n = Number(String(v ?? '').replace(/[^0-9.]/g, ''));
  return v && !isNaN(n) && n > 0 ? `$${n.toLocaleString()}` : '';
};
const join = (...parts) => parts.filter(Boolean).join(' ');

const isTitleTask = (name) => /^Send Title Initiation Email$/i.test(name || '');

export default function TitleInitiationFlow({ task, deal, ownerUid, transactionId, onSent }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [selected, setSelected] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [loading, setLoading] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');

  if (!isTitleTask(task.name || task.title)) return null;

  const uc = deal?.underContract || {};
  const client = { firstName: deal?.firstName, lastName: deal?.lastName, email: deal?.email, phone: deal?.phone };
  const second = firstSigner(deal);
  const coList = (deal?.coListAgents || []).filter(a => a.uid);
  const address = [deal?.propertyAddress, deal?.city, deal?.state, deal?.zipCode].filter(Boolean).join(', ');

  const btn = { display: 'inline-block', padding: '8px 14px', borderRadius: 6, fontWeight: 700, fontSize: 13, textDecoration: 'none', border: '1px solid #B8962E', background: '#B8962E', color: '#FFFFFF', cursor: 'pointer' };
  const btnCancel = { ...btn, background: '#FFFFFF', color: '#5B6270', borderColor: '#D5D9E0' };

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setLoading(true);
    try {
      const agent = ownerUid ? await getUserContact(ownerUid).catch(() => null) : null;
      const tc = user?.uid ? await getUserContact(user.uid).catch(() => null) : null;
      const coContacts = await Promise.all(coList.map(a => getUserContact(a.uid).catch(() => null)));
      const co = coContacts.find(Boolean) || {};
      const operations = coList[0] ? { name: coList[0].name || join(co.firstName, co.lastName), email: coList[0].email || co.email || '', phone: co.phone || '' } : { name: '', email: '', phone: '' };

      const isResale = deal?.type === 'Buyer' && deal?.propertyStatus !== 'new-construction';
      const coAgentName = join(co.firstName, co.lastName);
      const coAgentBlock = coAgentName ? `Additional Team Members to Copy (if applicable):\n${coAgentName}\n${co.phone || ''}\n${co.email || ''}\n\n` : '';
      const map = {
        tc_first_name: tc?.firstName || '',
        tc_full_name: join(tc?.firstName, tc?.lastName),
        tc_phone: tc?.phone || '',
        tc_email: tc?.email || '',
        title_agent_first_name: uc.titleAgentFirstName,
        agent_first_name: agent?.firstName || '',
        agent_last_name: agent?.lastName || '',
        agent_email: agent?.email || '',
        agent_phone_number: agent?.phone || '',
        property_address: address,
        cooperative_agent_first_name: uc.cooperativeAgentFirstName,
        cooperative_agent_last_name: uc.cooperativeAgentLastName,
        uc_dt: fmtDate(uc.underContractDate),
        earnest_money_amt: fmtMoney(uc.earnestMoneyAmount),
        earnest_money_due_dt: fmtDate(uc.earnestMoneyDueDate),
        due_diligence_deadline_dt: fmtDate(uc.dueDiligenceDeadline),
        second_emd_amount: fmtMoney(uc.secondEmdAmount),
        second_emd: fmtDate(uc.secondEmdDue),
        forecasted_closed_dt: fmtDate(uc.forecastedClosedDate),
        operations_name: operations.name,
        operations_phone: operations.phone,
        operations_email: operations.email,
        first_name: client.firstName,
        last_name: client.lastName,
        email: client.email,
        phone: client.phone,
        second_contact_name: join(second.firstName, second.lastName),
        second_contact_first_name: second.firstName || '',
        second_contact_last_name: second.lastName || '',
        second_contact_email: second.email,
        second_contact_phone: second.phone,
        coop_agent_email: uc.cooperatingAgentEmail,
        coop_agent_phone: uc.cooperatingAgentPhone,
        coop_tc_name: uc.cooperatingTcName,
        coop_tc_email: uc.cooperatingTcEmail,
        coop_tc_phone: uc.cooperatingTcPhone,
        buyer_names: uc.buyerNames,
        seller_names: deal?.sellerName || uc.sellerNames || '',
        co_agent_block: coAgentBlock,
      };

      setBody(fill(isResale ? RESALE_TEMPLATE : TEMPLATE, map).replace(/\n{3,}/g, '\n\n'));
      setSubject(`New file: ${address || 'under contract'}`);
      // To: the title agent and the other side's listing agent. CC: the team, the lender, and the other side's TC.
      setTo([uc.titleAgentEmail, uc.cooperatingAgentEmail].filter(Boolean).join(','));
      setCc([agent?.email, operations.email, uc.lenderEmail, uc.cooperatingTcEmail, tc?.email].filter(Boolean).join(','));
      const list = await listDealDocuments(ownerUid, transactionId).catch(() => []);
      setDocs(list);
      setSelected(Object.fromEntries(list.map((d) => [d.id, true])));
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const refreshDocs = async () => {
    const list = await listDealDocuments(ownerUid, transactionId).catch(() => []);
    setDocs(list);
    return list;
  };

  // Uploads a file from the computer into the transaction's documents, then selects it for this email.
  const onPickFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !ownerUid || !transactionId) return;
    setUploading(true);
    setUploadError('');
    try {
      const before = new Set(docs.map((d) => d.id));
      await uploadDealDocument(ownerUid, transactionId, file, user?.displayName || user?.email || '');
      const list = await refreshDocs();
      const added = list.filter((d) => !before.has(d.id));
      setSelected((prev) => ({ ...prev, ...Object.fromEntries(added.map((d) => [d.id, true])) }));
    } catch (err) {
      console.error('Could not upload attachment', err);
      setUploadError('That file did not upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const chosen = docs.filter((d) => selected[d.id]);

  const field = { width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6, border: '1px solid #D5D9E0', fontSize: 14, fontFamily: 'inherit', color: '#1A1A1A', marginBottom: 8 };

  return (
    <div style={{ margin: '6px 0' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 13, color: '#1A1A1A', fontWeight: 600 }}>Title initiation email</span>
        <button type="button" onClick={toggle} disabled={loading} style={btn}>{loading ? 'Loading...' : open ? 'Hide email' : 'Review and send'}</button>
      </div>
      {open && (
        <div style={{ marginTop: 8, padding: 10, border: '1px solid #D5D9E0', borderRadius: 8, background: '#FFFFFF' }}>
          {!uc.titleAgentEmail && <p style={{ fontSize: 12, color: '#B00020', margin: '0 0 8px' }}>No title agent email on the under contract form yet.</p>}
          <label style={{ fontSize: 12, color: '#5B6270' }}>To</label>
          <input value={to} onChange={(e) => setTo(e.target.value)} style={field} />
          <label style={{ fontSize: 12, color: '#5B6270' }}>CC</label>
          <input value={cc} onChange={(e) => setCc(e.target.value)} style={field} />
          <label style={{ fontSize: 12, color: '#5B6270' }}>Subject</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} style={field} />
          <label style={{ fontSize: 12, color: '#5B6270' }}>Message</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={18} style={field} />
          <p style={{ fontSize: 12, color: '#5B6270', margin: '0 0 4px', fontWeight: 600 }}>Attachments</p>
          {docs.length === 0 && <p style={{ fontSize: 12, color: '#5B6270', margin: '0 0 8px' }}>No documents on this transaction yet.</p>}
          {docs.map((d) => (
            <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '0 0 6px', color: '#1A1A1A' }}>
              <input type="checkbox" checked={!!selected[d.id]} onChange={(e) => setSelected({ ...selected, [d.id]: e.target.checked })} />
              <span>{d.name}</span>
            </label>
          ))}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', margin: '8px 0' }}>
            <label style={{ ...btnCancel, display: 'inline-block', opacity: uploading ? 0.6 : 1 }}>
              {uploading ? 'Uploading...' : 'Upload from my computer'}
              <input type="file" onChange={onPickFile} disabled={uploading} style={{ display: 'none' }} />
            </label>
            {chosen.map((d) => (
              <a key={`dl-${d.id}`} href={d.url} download={d.name} target="_blank" rel="noopener noreferrer" style={{ ...btnCancel, fontSize: 12 }}>Download {d.name}</a>
            ))}
          </div>
          {uploadError && <p style={{ fontSize: 12, color: '#B00020', margin: '0 0 8px' }}>{uploadError}</p>}
          {chosen.length > 0 && <p style={{ fontSize: 12, color: '#5B6270', margin: '0 0 8px' }}>Download the selected files, then attach them in your mail app. Each file is listed above with its own download button.</p>}
          <div style={{ height: 8 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <a href={`mailto:${encodeURIComponent(to)}?cc=${encodeURIComponent(cc)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`} style={btn} onClick={() => onSent && onSent()}>Send</a>
            <button type="button" onClick={() => setOpen(false)} style={btnCancel}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
